<template>
  <div class="statistics-page">
    <div class="statistics-header">
      <h2>数据统计</h2>
      <p class="update-time">最后更新: {{ updateTime }}</p>
    </div>
    
    <div class="statistics-content">
      <!-- 关键指标卡片 -->
      <div class="metrics-cards">
        <el-card class="metric-card" shadow="hover">
          <div class="card-content">
            <div class="metric-icon friends-icon">
              <i class="iconfont icon-friend"></i>
            </div>
            <div class="metric-info">
              <div class="metric-value">{{ statistics.friendsCount }}</div>
              <div class="metric-label">好友总数</div>
            </div>
          </div>
        </el-card>

        <el-card class="metric-card" shadow="hover">
          <div class="card-content">
            <div class="metric-icon groups-icon">
              <i class="iconfont icon-group"></i>
            </div>
            <div class="metric-info">
              <div class="metric-value">{{ statistics.groupsCount }}</div>
              <div class="metric-label">群组数量</div>
            </div>
          </div>
        </el-card>

        <el-card class="metric-card" shadow="hover">
          <div class="card-content">
            <div class="metric-icon messages-icon">
              <i class="iconfont icon-chat"></i>
            </div>
            <div class="metric-info">
              <div class="metric-value">{{ statistics.messagesCount }}</div>
              <div class="metric-label">消息总数</div>
            </div>
          </div>
        </el-card>

        <el-card class="metric-card" shadow="hover">
          <div class="card-content">
            <div class="metric-icon online-icon">
              <i class="iconfont icon-online"></i>
            </div>
            <div class="metric-info">
              <div class="metric-value">{{ statistics.todayFriends }}</div>
              <div class="metric-label">本日好友</div>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 详细统计信息 -->
      <div class="detailed-stats">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-card class="chart-card" shadow="hover">
              <template #header>
                <div class="card-header">
                  <span>好友统计</span>
                  <el-button type="text" @click="refreshData">刷新</el-button>
                </div>
              </template>
              <div class="friend-stats">
                <div class="stat-item">
                  <span class="stat-label">新增好友(本周):</span>
                  <span class="stat-value">{{ statistics.weeklyNewFriends }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">活跃好友:</span>
                  <span class="stat-value">{{ statistics.activeFriends }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">好友增长率:</span>
                  <span class="stat-value" :class="getGrowthClass(statistics.friendGrowthRate)">
                    {{ statistics.friendGrowthRate }}%
                  </span>
                </div>
              </div>
            </el-card>
          </el-col>

          <el-col :span="12">
            <el-card class="chart-card" shadow="hover">
              <template #header>
                <div class="card-header">
                  <span>消息统计</span>
                </div>
              </template>
              <div class="message-stats">
                <div class="stat-item">
                  <span class="stat-label">今日消息:</span>
                  <span class="stat-value">{{ statistics.todayMessages }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">未读消息:</span>
                  <span class="stat-value">{{ statistics.unreadMessages }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">消息频率:</span>
                  <span class="stat-value">{{ statistics.avgMessagesPerDay }}/天</span>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <!-- 趋势图 -->
      <el-card class="trend-chart-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <span>近7天趋势</span>
            <el-button type="text" @click="refreshTrend">刷新</el-button>
          </div>
        </template>
        <div ref="trendChart" class="trend-chart-container"></div>
      </el-card>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getStatistics, getStatisticsTrend } from '../api/user.js'

export default {
  name: 'Statistics',
  data() {
    return {
      statistics: {
        friendsCount: 0,
        groupsCount: 0,
        messagesCount: 0,
        todayFriends: 0,
        weeklyNewFriends: 0,
        activeFriends: 0,
        friendGrowthRate: 0,
        todayMessages: 0,
        unreadMessages: 0,
        avgMessagesPerDay: 0
      },
      updateTime: '',
      loading: false,
      chartInstance: null
    }
  },
  methods: {
    async loadStatistics() {
      this.loading = true;
      try {
        const response = await getStatistics();
        if (response) {
          this.statistics = { ...this.statistics, ...response };
        }
        this.updateTime = new Date().toLocaleString();
      } catch (error) {
        console.error('加载统计数据失败:', error);
        this.$message.error('加载统计数据失败');
      } finally {
        this.loading = false;
      }
    },

    async loadTrendData() {
      try {
        const response = await getStatisticsTrend();
        if (response && response.trend) {
          this.renderChart(response.trend);
        }
      } catch (error) {
        console.error('加载趋势数据失败:', error);
      }
    },

    renderChart(trendData) {
      if (!this.$refs.trendChart) return;

      if (!this.chartInstance) {
        this.chartInstance = echarts.init(this.$refs.trendChart);
      }

      const dates = trendData.map(item => {
        const parts = item.date.split('-');
        return parts[1] + '-' + parts[2];
      });
      const newFriends = trendData.map(item => item.newFriends || 0);
      const messageCounts = trendData.map(item => item.messageCount || 0);

      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#eee',
          borderWidth: 1,
          textStyle: { color: '#333', fontSize: 13 },
          axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(150, 150, 150, 0.08)' } }
        },
        legend: {
          data: ['新增好友', '消息数'],
          bottom: 0,
          itemWidth: 12,
          itemHeight: 12,
          textStyle: { color: '#666', fontSize: 13 }
        },
        grid: {
          top: 20,
          left: 10,
          right: 20,
          bottom: 40,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLine: { lineStyle: { color: '#ddd' } },
          axisTick: { show: false },
          axisLabel: { color: '#999', fontSize: 12 }
        },
        yAxis: [
          {
            type: 'value',
            name: '好友',
            nameTextStyle: { color: '#999', fontSize: 12 },
            splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } },
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: '#999', fontSize: 12 }
          },
          {
            type: 'value',
            name: '消息',
            nameTextStyle: { color: '#999', fontSize: 12 },
            splitLine: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: '#999', fontSize: 12 }
          }
        ],
        series: [
          {
            name: '新增好友',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            yAxisIndex: 0,
            lineStyle: { width: 2.5, color: '#764ba2' },
            itemStyle: { color: '#764ba2', borderWidth: 2, borderColor: '#fff' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(118, 75, 162, 0.25)' },
                { offset: 1, color: 'rgba(118, 75, 162, 0.02)' }
              ])
            },
            data: newFriends
          },
          {
            name: '消息数',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            yAxisIndex: 1,
            lineStyle: { width: 2.5, color: '#4facfe' },
            itemStyle: { color: '#4facfe', borderWidth: 2, borderColor: '#fff' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(79, 172, 254, 0.25)' },
                { offset: 1, color: 'rgba(79, 172, 254, 0.02)' }
              ])
            },
            data: messageCounts
          }
        ]
      };

      this.chartInstance.setOption(option);
    },

    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },

    refreshData() {
      this.loadStatistics();
      this.$message.success('数据已刷新');
    },

    refreshTrend() {
      this.loadTrendData();
      this.$message.success('趋势数据已刷新');
    },

    getGrowthClass(rate) {
      if (rate > 0) return 'positive';
      if (rate < 0) return 'negative';
      return 'neutral';
    }
  },
  mounted() {
    this.loadStatistics();
    this.$nextTick(() => {
      this.loadTrendData();
    });
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  }
}
</script>

<style scoped lang="scss">
.statistics-page {
  height: 100%;
  padding: 20px;
  background: #f5f7fa;
  overflow-y: auto;

  .statistics-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h2 {
      margin: 0;
      color: var(--im-color-primary);
      font-size: 24px;
    }
    
    .update-time {
      margin: 0;
      color: var(--im-text-color-secondary);
      font-size: 14px;
    }
  }

  .metrics-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 20px;

    .metric-card {
      border-radius: 8px;
      border: none;
      
      .card-content {
        display: flex;
        align-items: center;
        padding: 10px;
        
        .metric-icon {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
          font-size: 24px;
          color: white;
          
          &.friends-icon {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }
          &.groups-icon {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          }
          &.messages-icon {
            background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
          }
          &.online-icon {
            background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
          }
        }
        
        .metric-info {
          .metric-value {
            font-size: 28px;
            font-weight: bold;
            color: var(--im-text-color-primary);
            line-height: 1;
          }
          
          .metric-label {
            font-size: 14px;
            color: var(--im-text-color-secondary);
            margin-top: 5px;
          }
        }
      }
    }
  }

  .detailed-stats {
    margin-bottom: 20px;
    
    .chart-card {
      border-radius: 8px;
      border: none;
      
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 600;
      }
      
      .stat-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid var(--im-border-color-light);
        
        &:last-child {
          border-bottom: none;
        }
        
        .stat-label {
          color: var(--im-text-color-secondary);
        }
        
        .stat-value {
          font-weight: 600;
          color: var(--im-text-color-primary);
          
          &.positive {
            color: var(--im-color-success);
          }
          &.negative {
            color: var(--im-color-danger);
          }
          &.neutral {
            color: var(--im-text-color-secondary);
          }
        }
      }
    }
  }

  .trend-chart-card {
    border-radius: 8px;
    border: none;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: 600;
    }

    .trend-chart-container {
      width: 100%;
      height: 350px;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .statistics-page {
    padding: 10px;
    
    .metrics-cards {
      grid-template-columns: repeat(2, 1fr);
    }
    
    .detailed-stats .el-col {
      margin-bottom: 16px;
    }

    .trend-chart-container {
      height: 250px !important;
    }
  }
}
</style>
